/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { create } from 'zustand';
import { type GetDraftBotInfoAgwData } from '@coze-arch/idl/playground_api';

import {
  type SetterAction,
  setterActionFactory,
} from '../utils/setter-factory';

export type InferenceMode =
  | 'llm'
  | 'ontology'
  | 'document_workspace'
  | 'starrocks'
  | 'ontology_starrocks';

type BotInfoForInferenceMode = {
  inference_mode?: string;
  document_workspace?: { workspace?: { id?: string } };
  model_info?: { model_source?: string; parameters?: Record<string, string> };
};

const normalizeInferenceMode = (value?: string): InferenceMode | '' => {
  const trimmed = value?.trim();
  if (
    trimmed === 'llm' ||
    trimmed === 'ontology' ||
    trimmed === 'document_workspace' ||
    trimmed === 'starrocks' ||
    trimmed === 'ontology_starrocks'
  ) {
    return trimmed;
  }
  return '';
};

export function inferModeFromBotInfo(botInfo?: {
  inference_mode?: string;
  document_workspace?: { workspace?: { id?: string } };
  model_info?: { model_source?: string };
}): InferenceMode {
  const explicit = normalizeInferenceMode(botInfo?.inference_mode);
  if (explicit) {
    return explicit;
  }

  const workspaceId = botInfo?.document_workspace?.workspace?.id?.trim();
  if (workspaceId) {
    return 'document_workspace';
  }

  if (botInfo?.model_info?.model_source === 'ontology') {
    return 'ontology';
  }

  return 'llm';
}

const readModelSource = (
  modelInfo?: BotInfoForInferenceMode['model_info'],
): string | undefined => {
  if (!modelInfo) {
    return undefined;
  }
  if (modelInfo.model_source) {
    return modelInfo.model_source;
  }
  return modelInfo.parameters?.model_source;
};

export interface InferenceModeStore {
  mode: InferenceMode;
}

export const getDefaultInferenceModeStore = (): InferenceModeStore => ({
  mode: 'llm',
});

export interface InferenceModeAction {
  setInferenceMode: SetterAction<InferenceModeStore>;
  transformDto2Vo: (botData: GetDraftBotInfoAgwData) => InferenceModeStore;
  transformVo2Dto: (state: InferenceModeStore) => InferenceMode;
  initStore: (botData: GetDraftBotInfoAgwData) => void;
  clear: () => void;
}

export const useInferenceModeStore = create<
  InferenceModeStore & InferenceModeAction
>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      ...getDefaultInferenceModeStore(),
      setInferenceMode: setterActionFactory<InferenceModeStore>(set),
      transformDto2Vo: botData => {
        const botInfo = botData.bot_info as BotInfoForInferenceMode | undefined;
        const modelSource = readModelSource(botInfo?.model_info);

        return {
          mode: inferModeFromBotInfo({
            inference_mode: botInfo?.inference_mode,
            document_workspace: botInfo?.document_workspace,
            model_info: modelSource ? { model_source: modelSource } : undefined,
          }),
        };
      },
      transformVo2Dto: state => state.mode,
      initStore: botData => {
        const { transformDto2Vo } = get();
        set(transformDto2Vo(botData));
      },
      clear: () => {
        set({ ...getDefaultInferenceModeStore() });
      },
    })),
    {
      enabled: IS_DEV_MODE,
      name: 'botStudio.botDetail.inferenceMode',
    },
  ),
);
